/**
 * Fuel-usage presentation helpers.
 *
 * The fuel-usage list is paginated, so cross-row metrics are derived only from
 * the rows currently in view. Each vehicle's oldest row on the page therefore
 * has no baseline and is flagged accordingly.
 */

/**
 * Enrich fuel-usage rows with per-vehicle, tank-to-tank derived metrics.
 *
 * For two consecutive refuels of the same vehicle:
 *  - `distance`   = odometer difference between them (km run).
 *  - `efficiency` = distance covered per litre of the *previous* tank (km/L).
 *
 * @param {Array<object>} rows - Raw fuel-usage records from the API.
 * @returns {Array<object>} The same rows, each with a `metrics` object:
 *   `{ distance, efficiency, prevMileage, hasBaseline }`.
 */
export const enrichFuelUsages = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return rows
  }

  const groups = new Map()
  rows.forEach(row => {
    const key = row?.vehicle_id ?? row?.vehicle?.id ?? "unknown"
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key).push(row)
  })

  const metricsById = new Map()

  groups.forEach(group => {
    const ordered = [...group].sort((a, b) => {
      const dateDiff = new Date(a?.uses_date) - new Date(b?.uses_date)
      return dateDiff !== 0 ? dateDiff : (a?.id ?? 0) - (b?.id ?? 0)
    })

    ordered.forEach((row, index) => {
      const prev = index > 0 ? ordered[index - 1] : null
      const mileage = Number(row?.mileage)
      const prevMileage = prev ? Number(prev?.mileage) : null
      const prevFuel = prev ? Number(prev?.fuel_qty) : null

      let distance = null
      let efficiency = null

      if (prev && Number.isFinite(mileage) && Number.isFinite(prevMileage)) {
        distance = mileage - prevMileage

        if (Number.isFinite(prevFuel) && prevFuel > 0 && distance >= 0) {
          efficiency = distance / prevFuel
        }
      }

      metricsById.set(row?.id, {
        distance,
        efficiency,
        prevMileage,
        hasBaseline: !!prev,
      })
    })
  })

  return rows.map(row => ({
    ...row,
    metrics: metricsById.get(row?.id) ?? {
      distance: null,
      efficiency: null,
      prevMileage: null,
      hasBaseline: false,
    },
  }))
}

/**
 * Format a numeric value with thousands separators.
 *
 * @param {number|string} value
 * @param {number} maximumFractionDigits
 * @returns {string}
 */
export const formatNumber = (value, maximumFractionDigits = 0) => {
  const num = Number(value)
  if (!Number.isFinite(num)) {
    return "-"
  }
  return num.toLocaleString(undefined, { maximumFractionDigits })
}
